<template>
  <section class="gallery-section">
    <!-- ---------- HEAD ---------- -->
    <div class="section-head">
      <div class="sh-inner">
        <h2>RSKJP Melinda Cardio Vascular Center</h2>
        <h1>Galeri Melinda CVC</h1>
        <p>
          RSKJP Melinda Cardio Vascular Center mempunyai fasilitas kesehatan yang bervariasi dan terbaru, serta peralatan medis yang sudah sesuai standar. Mempertahankan kualitas rumah sakit dengan menyediakan servis yang terbaik, aman, nyaman, ramah, efektif, dan efisien. RSKJP Melinda Cardio Vascular Center memiliki bangunan dengan desain interior estetik didukung dengan interior bernilai seni tinggi. Suasana rumah sakit bahkan terkesan seperti hotel bintang 5.
        </p>
      </div>
    </div>

    <!-- ---------- BODY ---------- -->
    <div class="section-body" v-if="galleries && currentCategory">
      <!-- DESKTOP SIDEBAR -->
      <aside class="sb-panel desktop-only">
        <div class="sb-panel-inner">
          <h2>Kategori Galeri</h2>
          <div class="sb-panel-list">
            <button
              v-for="cat in galleries"
              :key="cat.slug"
              :class="['spl-part', { active: cat.slug === selectedSlug }]"
              @click="selectedSlug = cat.slug"
            >
              {{ cat.name }}
            </button>
          </div>
        </div>
      </aside>

      <!-- MOBILE SELECT -->
      <div class="sb-panel-mobile mobile-only">
        <select v-model="selectedSlug" class="category-select">
          <option
            v-for="cat in galleries"
            :key="cat.slug"
            :value="cat.slug"
          >
            {{ cat.name }}
          </option>
        </select>
      </div>

      <!-- CONTENT GRID -->
      <div class="sb-content">
        <div class="sci-grid">
          <div
            v-for="(content, idx) in currentCategory.contents"
            :key="content.name"
            class="sci-card"
            @click="openCarousel(idx)"
          >
            <img :src="content.image" :alt="content.name" />
            <div class="sci-card-overlay">
              <h3>{{ content.name }}</h3>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 🚀 ADD: Swiper modal -->
    <teleport to="body">
      <div
        v-if="showCarousel"
        class="swiper-modal"
        @keydown.esc="closeCarousel"
        @wheel.passive="onWheel"
      >
        <div class="swiper-modal-backdrop" @click.self="closeCarousel" />
        <button class="swiper-close" @click="closeCarousel">&times;</button>
        <swiper
          :modules="swiperModules"
          :initial-slide="startIndex"
          :navigation="false"
          :pagination="{ clickable: true }"
          class="swiper-wrapper-custom"
        >
          <swiper-slide v-for="(slide, i) in currentCategory.contents" :key="i">
            <img :src="slide.image" :alt="slide.name" />
          </swiper-slide>
        </swiper>
      </div>
    </teleport>
  </section>
</template>

<script setup>
// ------------------------------------------------------------
// 🚀 ADD: Swiper integration & lightbox carousel
// ------------------------------------------------------------
import { ref, computed, watchEffect } from 'vue'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation, Pagination, Keyboard } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'

import { useGalleryStore } from '@/stores/gallery'

// Swiper component registration (for <script setup>)
const swiperModules = [Navigation, Pagination, Keyboard]

const galleryStore = useGalleryStore()
galleryStore.initGallery()
const galleries = computed(() => galleryStore.galleries)

const selectedSlug = ref('')
watchEffect(() => {
  if (!selectedSlug.value && galleries.value.length) {
    selectedSlug.value = galleries.value[0].slug
  }
})

const currentCategory = computed(() =>
  galleries.value.find((c) => c.slug === selectedSlug.value)
)

// -------------- Swiper modal state -------------
const showCarousel = ref(false)
const startIndex = ref(0)

function openCarousel(idx) {
  startIndex.value = idx
  showCarousel.value = true
  // lock body scroll
  document.body.style.overflow = 'hidden'
}
function closeCarousel() {
  showCarousel.value = false
  document.body.style.overflow = ''
}
// allow scroll/zoom inside modal without closing
function onWheel(e) {
  const el = e.target.closest('.swiper-wrapper-custom')
  if (!el) e.preventDefault()
}
</script>

<style scoped>
/******************* HEAD *******************/
.section-head {
  padding: 2.5rem 10%;
  background: var(--navy);
  color: var(--white);
}
.sh-inner h2 {
  font-size: 1.125rem;
  margin: 0;
  color: var(--cream);
}
.sh-inner h1 {
  font-size: 3.25rem;
  margin: 1rem 0 1rem 0;
}

/******************* LAYOUT *******************/
.section-body {
  display: flex;
  flex-wrap: wrap;
  background: var(--white);
}
.sb-panel {
  width: 30%;
}
.sb-panel-inner {
  background: var(--cream-bg);
  padding: 1rem 1rem 1rem 32%;
}
/* side list */
.spl-part {
  display: block;
  width: 100%;
  padding: 0.5rem 0;
  text-align: left;
  border: none;
  background: none;
  font-size: 1rem;
  font-weight: 600;
  border-bottom: 1px solid var(--grey);
  cursor: pointer;
  transition: color 0.2s;
}
.spl-part.active {
  color: var(--primary);
}
.spl-part:hover {
  color: var(--primary);
}
/* mobile select */
.sb-panel-mobile {
  width: 100%;
  padding: 1rem 2.5%;
}
.category-select {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid var(--softgrey);
  border-radius: 6px;
  font-size: 1rem;
}
/* content grid */
.sb-content {
  width: 70%;
}
.sci-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1rem;
  padding: 0.5rem;
  transition: 0.2s;
}
.sci-card {
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  cursor: pointer; /* 🚀 ADD: pointer for click */
}
.sci-card img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}
.sci-card:hover img {
  transform: scale(1.05);
}
.sci-card-overlay {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 0.75rem;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent);
  color: var(--white);
}
.sci-card-overlay h3 {
  margin: 0;
  font-size: 1.1rem;
}

/******************* SWIPER MODAL *******************/
.swiper-modal {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
}
.swiper-modal-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
}
.swiper-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 2rem;
  color: var(--white);
  z-index: 1001;
  cursor: pointer;
}
.swiper-wrapper-custom {
  width: 90vw;
  max-width: 1000px;
  height: 90vh;
  z-index: 1001;
}
.swiper-slide img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/******************* RESPONSIVE *******************/
@media (max-width: 1500px) {
  .section-head {
    padding: 2.5rem 5%;
  }
  .sb-panel-inner {
    padding-left: 15%;
  }
}
@media (max-width: 750px) {
  .sh-inner h1 {
    font-size: 1.5rem;
  }
  /* hide desktop panel, show select */
  .desktop-only {
    display: none;
  }
  .mobile-only {
    display: block;
  }
  .sb-panel-mobile {
    padding: 1rem 2.5%;
  }
  .sb-panel {
    display: none;
  }
  .sb-content,
  .sb-panel-mobile {
    width: 100%;
  }
  .sci-grid {
    grid-template-columns: repeat(auto-fill, minmax(270px, 1fr));
  }
  .swiper-wrapper-custom {
    width: 100vw;
    height: 80vh;
  }
}
@media (min-width: 751px) {
  .mobile-only {
    display: none;
  }
}
/* extra small */
@media (max-width: 450px) {
  .section-head {
    padding: 1.5rem 2.5% 0 2.5%;
  }
}
</style>
